<template>
  <div class="page t-container">
    <span class="page-subheading t-text-xs t-text-gray-600 t-font-medium">
      SETTINGS | LOCATIONS
    </span>
    <div class="page-heading" role="heading">
      <h1 class="page-heading__header">Locations</h1>
      <div class="t-mt-4 md:t-mt-0">
        <button
          @click="onNewLocation"
          class="t-align-right t-bg-dodger-blue t-px-8 t-py-3"
        >
          <span class="t-text-base t-font-bold t-text-white">
            <span class="t-text-sm">+</span>
            New Location
          </span>
        </button>
      </div>
    </div>

    <!-- Locations Menu -->
    <div class="tabs-container">
      <div class="tabs-menu t-space-x-4 md:t-space-x-6" role="menu">
        <a
          role="menuitem"
          @click="loadLocations(1)"
          :class="{ 'tab-active': currentTab === 'ALL_LOCATIONS' }"
        >
          All
        </a>
        <a
          role="menuitem"
          @click="getPickupLocation(1)"
          :class="{ 'tab-active': currentTab === 'PICKUP_LOCATIONS' }"
        >
          Pickup
        </a>
        <a
          role="menuitem"
          @click="getDeliveryLocation(1)"
          :class="{ 'tab-active': currentTab === 'DELIVERY_LOCATIONS' }"
        >
          Delivery
        </a>
      </div>
      <div class="t-inline-block" style="margin-top: 2px">
        <autocomplete
          id="location-search"
          results-property="value"
          results-display="text"
          placeholder="Search"
          source="/api/userlocations?q="
          @selected="onSearchSelected"
        />
      </div>
    </div>
    <!-- End of Locations Menu -->

    <!-- Location Cards -->
    <section>
      <loading-alert :loading="isLoading" text="Loading locations..." />
      <div v-if="!locationData" class="t-mt-8 t-flex t-justify-center">
        <p class="t-text-xl">No Locations Yet</p>
      </div>

      <div class="t-flex t-justify-end t-mb-2">
        <pagination
          :data="locationData"
          @pagination-change-page="getDataLocation"
          :limit="5"
        >
          <p v-for="(l, index) in locationData.data" :key="index"></p>
        </pagination>
      </div>

      <location-card
        v-if="defaultPickup"
        :location="defaultPickup"
        @defaultChanged="reloadLocations"
        @editLocation="onEditLocation"
        @deleteLocation="deleteLocation"
        class="t-mb-4"
      />

      <location-card
        v-for="(location, index) in locations"
        :key="index"
        :location="location"
        @defaultChanged="reloadLocations"
        @editLocation="onEditLocation"
        @deleteLocation="deleteLocation"
        class="t-mb-4"
      />
    </section>

    <!-- Location Form Modal -->
    <location-form-modal
      v-if="isShowingSaveLocationModal"
      :is-showing-modal="isShowingSaveLocationModal"
      :is-creating-location="isCreatingLocation"
      :location-to-edit="selectedLocation"
      @cancelled="formCancelled"
      @submitted="formSubmitted"
    />

    <confirm-delete
      v-if="isShowingDeleteModal"
      :showing="isShowingDeleteModal"
      title="Delete this location?"
      :text="
        'Are you sure you want to delete ' + this.selectedLocation.name + '?'
      "
      :deleting="isDeleting"
      @confirmed="deleteConfirmed"
      @cancelled="deleteCancelled"
    />
  </div>
</template>

<script>
import Autocomplete from "../../utils/Autocomplete";
import LocationCard from "./components/LocationCard";
import ConfirmDelete from "../../utils/ConfirmDelete";
import LocationFormModal from "./components/LocationFormModal";

export default {
  name: "locations",
  components: {
    Autocomplete,
    LocationCard,
    ConfirmDelete,
    LocationFormModal
  },
  data() {
    return {
      isLoading: true,
      currentTab: null,
      defaultPickup: null,
      locations: [],
      locationData: {},
      selectedLocation: null,
      isCreatingLocation: true,
      isShowingSaveLocationModal: false,
      isDeleting: false,
      isShowingDeleteModal: false,
      currPageNumber: 1
    };
  },
  created() {
    this.ALL_LOCATIONS = "ALL_LOCATIONS";
    this.PICKUP_LOCATIONS = "PICKUP_LOCATIONS";
    this.DELIVERY_LOCATIONS = "DELIVERY_LOCATIONS";
    this.currentTab = this.ALL_LOCATIONS;

    this.loadLocations(this.locationData.currentPage);
    this.loadDefaultPickup();
  },
  methods: {
    /**
     * Loads locations for the current page.
     * @param page - Current page.
     */
    getDataLocation(page) {
      this.currPageNumber = page;
      this.currentTab === "ALL_LOCATIONS"
        ? this.loadLocations(page)
        : this.currentTab === "PICKUP_LOCATIONS"
        ? this.getPickupLocation(page)
        : this.getDeliveryLocation(page);
    },

    loadLocations(page) {
      this.getLocation("/settings/locations?page=" + page);
      this.currentTab = "ALL_LOCATIONS";
    },

    getPickupLocation(page) {
      this.getLocation("/settings/locations/pickup?page=" + page);
      this.currentTab = "PICKUP_LOCATIONS";
    },

    getDeliveryLocation(page) {
      this.getLocation("/settings/locations/delivery?page=" + page);
      this.currentTab = "DELIVERY_LOCATIONS";
    },

    getLocation(url) {
      this.isLoading = true;
      axios
        .get(url)
        .then(response => {
          response.data.data.map((location, index) => {
            if (location.defaults.pickup) {
              response.data.data.splice(index, 1);
            }
          });

          this.locations = response.data.data;
          this.locationData.current_page =
            response.data.meta.pagination.current_page;
          this.locationData.last_page =
            response.data.meta.pagination.total_pages;
          this.locationData.next_page_url =
            response.data.meta.pagination.links.next;
          this.locationData.per_page = response.data.meta.pagination.per_page;
          this.locationData.prev_page_url =
            response.data.meta.pagination.links.previous;
          this.locationData.total = response.data.meta.pagination.total;
          this.isLoading = false;
        })
        .catch(() => {
          Event.$emit("serverError");
          this.isLoading = false;
        });
    },

    loadDefaultPickup() {
      // Loads the default location
      axios
        .get("/settings/locations/default-pickup")
        .then(response => {
          this.defaultPickup = response.data.data;
        })
        .catch(() => {
          Event.$emit("serverError");
        });
    },

    async onSearchSelected(locationResult) {
      // Load and fetch the location using its ID
      this.isLoading = true;
      try {
        let res = await axios.get(
          `/settings/locations/id/${locationResult.selectedObject.value}`
        );
        let location = await res.data.data[0];
        this.onEditLocation(location);
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },
    // ----- Menu Related Functions -----
    formCancelled() {
      // Reset
      this.isCreatingLocation = true;
      this.selectedLocation = null;
      this.isShowingSaveLocationModal = false;
    },

    onNewLocation() {
      this.isCreatingLocation = true;
      this.isShowingSaveLocationModal = true;
    },

    onEditLocation(location) {
      this.isCreatingLocation = false;
      this.selectedLocation = location;
      this.isShowingSaveLocationModal = true;
    },

    formSubmitted() {
      this.selectedLocation = null;
      this.isShowingSaveLocationModal = false;
      this.reloadLocations();
    },

    deleteLocation(location) {
      this.selectedLocation = location;
      this.isShowingDeleteModal = true;
    },

    showDeleteModal(location) {
      this.selectedLocation = location;
      this.isShowingDeleteModal = true;
    },

    deleteCancelled() {
      this.selectedLocation = null;
      this.isShowingDeleteModal = false;
    },

    deleteConfirmed() {
      this.isDeleting = true;

      axios
        .delete("/settings/locations/detach/" + this.selectedLocation.id, {
          headers: { Accept: "application/json" }
        })
        .then(() => {
          this.selectedLocation = null;
          this.isShowingDeleteModal = false;
          this.isDeleting = false;
          this.$swal({
            title: "Location Deleted!",
            type: "success",
            confirmButtonText: "Okay"
          });
          this.reloadLocations();
        })
        .catch(() => {
          this.isShowingDeleteModal = false;
          this.isDeleting = false;
        });
    },

    reloadLocations() {
      // this.loadLocations(this.locationData.currentPage);
      this.loadLocations(this.currPageNumber);
      this.loadDefaultPickup();
    },

    onChangeTab(tab) {
      this.currentTab = tab;
    }
  }
};
</script>

<style lang="scss">
$md: 768px;
$dark-gray: #1f2937;

.location-form-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  label {
    margin-bottom: 0;
    font-weight: 600;
    color: $dark-gray;
  }

  > input,
  p.value {
    border: 1px solid #cbd5e0;
    border-radius: 0.375rem;
    width: 100%;
    padding: 0.25rem 0.5rem;

    :focus {
      outline: none;
      border: 1px solid #667eea;
    }
  }

  &__heading {
    display: flex;
    flex-direction: column;

    @media (min-width: $md) {
      flex-direction: row;
      justify-content: space-between;
    }
  }
}

.location-form-option {
  display: flex;
  margin-bottom: 0.5rem;

  > input {
    margin-top: 0.25rem;
    margin-right: 1rem;
    width: 1rem;
    height: 1rem;
  }

  label {
    margin-bottom: 0;
    font-weight: 500;
    color: $dark-gray;
  }
}

.tab-active {
  border-bottom: 2px solid #17a8e2;
}
</style>
